import React, { useState, useEffect } from "react";
import AutoScribeForm from "./AutoScribeForm";
import IdeaSentinel from "./Ideasenital";
import BacklogCurator from "./BacklogCurator";
import Guidelines from "./Guidelines";
import ReviewProcessComponent from "./ReviewProcessComponent";
import { logout } from "../auth";
import Admin from "./Admin";
import { Link } from "react-router-dom";
import TriagePage from "../pages/TriagePage";
import DiscoveryPage from "../pages/DiscoveryPage";
import POCPage from "../pages/POCPage";
import AoAPage from "../pages/AoAPage";
import BpmImprovementPage from "../pages/BpmImprovementPage";
import BpmImportPage from "../pages/BpmImportPage";
import RequirementsPage from "../pages/RequirementsPage";
import TABPage from "../pages/TABPage";
import SecurityPage from "../pages/SecurityPage";
import ExecutionPage from "../pages/ExecutionPage";
import ServiceNowTicketsPage from "../pages/ServiceNowTicketsPage";
import PortfolioPage from "../pages/PortfolioPage";
import DemandArtifactsPage from "../pages/DemandArtifactsPage";
import CyberPage from "../pages/CyberPage";
import ChargebackPage from "../pages/ChargebackPage";
import DemandEditPage from "../pages/DemandEditPage";
import TasksPage from "../pages/TasksPage";
import IdeasPageHeader from "./IdeasPageHeader";
import DemandPageHeader from "./DemandPageHeader";
import TechnologyPage from "../pages/TechnologyPage";
import ChatBotPage from "../pages/ChatBotPage";
import DemandDashboard from "./DemandDashboard";
import DataVisualization from "../features/technology/DataVisualization";
import Technology from "../features/technology/Technology";
import HomePage from "./Home";
import Profile from "./Profile";
import Settings from "./Settings";
import ReportsAnalytics from "./ReportsAnalytics";

import logo from "../assets/logo.png";

// TRM v2 Components
import TRMOverview from "./TRM/TRMOverview";
import TRMAnalyticsDashboard from "./TRM/TRMAnalyticsDashboard";
import ProductPortfolio from "./TRM/ProductPortfolio";
import SoftwareLicenses from "./TRM/SoftwareLicenses";
import FinancialTracking from "./TRM/FinancialTracking";
import ConsolidationAnalysis from "./TRM/ConsolidationAnalysis";
import TraConformancePage from "./TRM/TraConformancePage";
import DataSourcesPage from "./TRM/DataSourcesPage";
import MegabytePage from "./TRM/MegabytePage";
import LifecycleManagement from "./TRM/LifecycleManagement";
import TaxonomyBrowser from "./TRM/TaxonomyBrowser";

import {
  Activity, Archive, ArrowLeft, BarChart3, Bell, Binoculars, BookOpen, BookOpenCheck, Bot, Brain, Bug, Building2, Calendar, CalendarClock, CheckCircle, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ClipboardCheck, ClipboardList, Clock, Cpu, Crosshair, Database, DollarSign, ExternalLink, Eye, FileCode2, FileText, Filter, FolderTree, Gavel, GitBranch, HeartPulse, HelpCircle, Home, Info, Key, Landmark, Layers, Lightbulb, Link as LinkIcon, List, LogOut, Menu, MessageCircle, Orbit, Package, PieChart, RefreshCw, Rocket, Scale, Search, Settings as SettingsIcon, Shield, ShieldAlert, ShieldCheck, Star, Target, Ticket, TrendingUp, Upload, User, User as UserIcon, Users, Workflow, X,
  GitCompareArrows,
} from "lucide-react";

// API service with real endpoint
const apiService = {
  async getDashboardStats() {
    const endpoint = `/api/dashboard/get_dashboard`;

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        totalIdeas: data["Total Ideas"] || 0,
        pendingReview: data["Pending Reviews"] || 0,
        approvedIdeas: data["Approved Ideas"],
        avgReviewTime: data["Average Review Time (days)"] || 0,
        approvalRate: data["Percent of Ideas Approved"] || 0,
        newIdeas: data["Newly Added"] || 0,
        ideasByStatus: [
          {
            status: "Submission",
            count: data["Submission"] || 0,
            color: "var(--cares-text-note)",
          },
          {
            status: "Supervisor Review",
            count: data["Supervisor Review"] || 0,
            color: "var(--cares-orange)",
          },
          {
            status: "ITPM Review",
            count: data["ITPM Review"] || 0,
            color: "var(--cares-purple)",
          },
          {
            status: "Backlog Triage",
            count: data["Backlog Triage"] || 0,
            color: "var(--cares-blue)",
          },
          {
            status: "Final Submission",
            count: data["Final Submission"] || 0,
            color: "var(--cares-green)",
          },
        ],
        recentActivity: data["Recent Activity"]
          ? Object.values(data["Recent Activity"]).map((activity) => ({
              type: "new_idea",
              title: activity["Title & Sponsor"] || "Untitled Idea",
              author: activity["Submitted By"] || "Unknown",
              timestamp: `${activity["Ago in minutes"] || 0} minutes ago`,
            }))
          : [],
      };
    } catch (error) {
      console.error("API Error:", error);
      throw error; // Re-throw to let the component handle the error
    }
  },

  async getIdeaManagementData() {
    const endpoint = `/api/idea/get_ideamanagement`;

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("API Error:", error);
      throw error; // Re-throw to let the component handle the error
    }
  },
};

// Header Component
const Header = ({
  user = { name: "System Administrator", role: "Admin", email: "admin@aiea.gov" },
  currentTab = "dashboard",
  sidebarCollapsed,
  toggleSidebar,
  setActiveTab
}) => {
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);

  const notifications = [
    { id: 1, title: "New idea submitted", time: "5 min ago", unread: true },
    { id: 2, title: "Review completed", time: "1 hour ago", unread: true },
    { id: 3, title: "System update available", time: "2 hours ago", unread: true },
  ];

  // Tooltip content for home page
  const homePageTooltips = [
    {
      title: "Welcome to AIEA!",
      description: "This is your central hub for managing ideas, demands, and technology initiatives."
    },
    {
      title: "Quick Start",
      description: "Use the sidebar to navigate between Ideas Management, Demand Management, and other tools."
    },
    {
      title: "Submit an Idea",
      description: "Click on 'Ideas Management' → 'Idea Submission' to submit a new idea for review."
    },
    {
      title: "Track Progress",
      description: "Monitor your submissions and reviews in the Ideas Dashboard."
    }
  ];

  // Determine title, breadcrumb, and icon based on current tab
  const getHeaderContent = () => {
    if (currentTab.startsWith("admin-")) {
      const section = currentTab.split("-").slice(1).join("-");
      const formattedSection = section
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      // Map admin sections to icons
      let icon = null;
      if (currentTab === "admin-users" || currentTab === "admin-user-management") {
        icon = Users;
      } else if (currentTab === "admin-roles" || currentTab === "admin-role-management") {
        icon = Key;
      } else if (currentTab === "admin-persona-management") {
        icon = Users;
      } else if (currentTab === "admin-questionnaire") {
        icon = ClipboardList;

      } else if (currentTab === "admin-data-lineage") {
        icon = GitBranch;
      } else if (currentTab === "admin-mfa") {
        icon = Shield;
      } else if (currentTab === "admin-signin-logs" || currentTab === "admin-audit-logs") {
        icon = ClipboardList;
      } else if (currentTab === "admin-system-health") {
        icon = HeartPulse;
      } else if (currentTab === "admin-system-info") {
        icon = Info;
      }

      return {
        title: formattedSection,
        breadcrumb: `Home > Admin > ${formattedSection}`,
        icon: icon,
      };
    }

    switch (currentTab) {
      case "home":
        return {
          title: "Home",
          breadcrumb: "Home",
        };
      case "demand-edit":
        return {
          title: "My Tasks",
          breadcrumb: "Home > Tasks",
        };
      case "dashboard":
        return {
          title: "Ideas Dashboard",
          breadcrumb: "Home > Ideas Dashboard",
        };
      case "demand-dashboard":
        return {
          title: "Demand Management Dashboard",
          breadcrumb: "Home > Demand Management > Dashboard",
        };
      case "approved-ideas":
        return {
          title: "Approved Ideas",
          breadcrumb: "Home > Demand Management > Approved Ideas",
        };
      case "ideas":
      case "autoscribe":
      case "review":
      case "sentinel":
      case "backlogcurator":
      case "guidelines":
        return {
          title: "Ideas Management",
          breadcrumb: "Home > Ideas Management",
        };
      case "poc":
        return {
          title: "POC Checkpoint Board",
          breadcrumb: "Home > Demand Management > Enhanced Discovery (POC)"
        };
      case "discovery":
        return {
          title: "Discovery Planner",
          breadcrumb: "Home > Demand Management > Discovery"
        };
      case "triage":
         return {
           title: "Triage",
           breadcrumb: "Home > Demand Management > Triage"
         };
      case "requirements":
        return {
          title: "Requirements Builder",
          breadcrumb: "Home > Demand Management > AI Requirements"
        };
      case "tab":
        return {
          title: "TAB Decision Panel",
          breadcrumb: "Home > Demand Management > TAB Decision"
        };
      case "security":
        return {
          title: "Security Pre-screen",
          breadcrumb: "Home > Demand Management > Security Pre-Screen"
        };
      case "technology":
        return {
          title: "Technical Reference Model",
          breadcrumb: "Home > Technical Reference Model"
        };
      case "trm-portal":
        return {
          title: "TRM Portal",
          breadcrumb: "Home > Technical Reference Model > TRM Portal"
        };
      case "chatbot":
        return {
          title: "Tool Analysis Assistant",
          breadcrumb: "Home > Technical Reference Model > Tool Analysis Assistant"
        };
      case "trm-visualization":
        return {
          title: "Data Visualization",
          breadcrumb: "Home > Technical Reference Model > Data Visualization"
        };
      case "execution":
        return {
          title: "Execution Preview",
          breadcrumb: "Home > Demand Management > Execution Handoff"
        };
      case "bpm":
        return {
          title: "Process Analysis",
          breadcrumb: "Home > BPM Optimization > Process Analysis",
        };
      case "bpm-import":
        return {
          title: "Import Process",
          breadcrumb: "Home > BPM Optimization > Import Process",
        };
      default:
        return {
          title: "Home",
          breadcrumb: "Home",
        };
    }
  };

  const { title, breadcrumb, icon } = getHeaderContent();
  const IconComponent = icon;

  return (
    <header style={{
      background: 'var(--cares-surface-card)',
      borderBottom: '1px solid var(--cares-line-card)',
      padding: '4px 24px',
      position: 'sticky',
      top: 0,
      zIndex: 40,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Left side - Logo and Title */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <img
            src={logo}
            alt="AIEA Logo"
            style={{
              height: '38px',
              width: 'auto'
            }}
          />
          {IconComponent && (
            <IconComponent style={{
              width: '24px',
              height: '24px',
              color: 'var(--cares-blue)'
            }} />
          )}
          <h1 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: 'var(--cares-text-1)',
            margin: 0
          }}>
            {title}
          </h1>
        </div>

        {/* Right side - Icons and profile */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Notifications */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              style={{
                padding: '8px',
                borderRadius: '8px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Bell style={{ width: '20px', height: '20px', color: 'var(--cares-text-label)' }} />
              <span style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                background: 'var(--cares-orange)',
                color: 'var(--cares-text-max)',
                fontSize: '10px',
                fontWeight: 'bold',
                borderRadius: '50%',
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {notifications.filter(n => n.unread).length}
              </span>
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: 'var(--cares-surface-card)',
                borderRadius: '12px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                border: '1px solid var(--cares-line-card)',
                width: '320px',
                zIndex: 50
              }}>
                <div style={{
                  padding: '16px',
                  borderBottom: '1px solid var(--cares-line-card)'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--cares-text-1)',
                    margin: 0
                  }}>
                    Notifications
                  </h3>
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--cares-line)',
                        cursor: 'pointer',
                        background: notif.unread ? 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))' : 'var(--cares-surface-card)',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = notif.unread ? '#eff6ff' : 'white'}
                    >
                      <p style={{
                        fontSize: '14px',
                        fontWeight: notif.unread ? '600' : '400',
                        color: 'var(--cares-text-1)',
                        margin: '0 0 4px 0'
                      }}>
                        {notif.title}
                      </p>
                      <p style={{
                        fontSize: '12px',
                        color: 'var(--cares-text-label)',
                        margin: 0
                      }}>
                        {notif.time}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Admin */}
          <button
            onClick={() => setActiveTab('admin')}
            style={{
              padding: '8px',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            title="Admin Panel"
          >
            <Shield style={{ width: '20px', height: '20px', color: 'var(--cares-text-label)' }} />
          </button>

          {/* Help/Tooltip - Only show on home page */}
          {currentTab === 'home' && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowTooltip(!showTooltip)}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  border: 'none',
                  background: showTooltip ? 'color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card))' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!showTooltip) e.currentTarget.style.background = 'var(--cares-surface-board)';
                }}
                onMouseLeave={(e) => {
                  if (!showTooltip) e.currentTarget.style.background = 'transparent';
                }}
                title="Quick Tips"
              >
                <HelpCircle style={{ width: '20px', height: '20px', color: showTooltip ? 'var(--cares-blue)' : 'var(--cares-text-label)' }} />
              </button>

              {/* Tooltip Dropdown */}
              {showTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  width: '380px',
                  background: 'var(--cares-surface-card)',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  border: '1px solid var(--cares-line-card)',
                  zIndex: 50,
                  overflow: 'hidden'
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '16px',
                    background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-purple) 100%)',
                    color: 'var(--cares-text-max)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <HelpCircle style={{ width: '20px', height: '20px' }} />
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: 0
                      }}>
                        Quick Tips
                      </h3>
                    </div>
                    <p style={{
                      fontSize: '13px',
                      margin: 0,
                      opacity: 0.9
                    }}>
                      Get started with AIEA
                    </p>
                  </div>

                  {/* Tips List */}
                  <div style={{
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {homePageTooltips.map((tip, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '16px',
                          borderBottom: index < homePageTooltips.length - 1 ? '1px solid var(--cares-line)' : 'none'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: 'color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card))',
                            color: 'var(--cares-blue)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {index + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <h4 style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: 'var(--cares-text-1)',
                              margin: '0 0 4px 0'
                            }}>
                              {tip.title}
                            </h4>
                            <p style={{
                              fontSize: '13px',
                              color: 'var(--cares-text-label)',
                              margin: 0,
                              lineHeight: '1.5'
                            }}>
                              {tip.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div style={{
                    padding: '12px 16px',
                    background: 'var(--cares-surface-board)',
                    borderTop: '1px solid var(--cares-line-card)',
                    textAlign: 'center'
                  }}>
                    <button
                      onClick={() => setShowTooltip(false)}
                      style={{
                        padding: '6px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'var(--cares-blue)',
                        color: 'var(--cares-text-max)',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
                    >
                      Got it!
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Profile */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px 6px 6px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--cares-surface-board)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-line-card)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-purple) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--cares-text-max)',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                SA
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--cares-text-1)'
                }}>
                  {user.name}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--cares-text-label)'
                }}>
                  {user.role}
                </div>
              </div>
              <ChevronDown style={{ width: '16px', height: '16px', color: 'var(--cares-text-label)' }} />
            </button>

            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: 'var(--cares-surface-card)',
                borderRadius: '12px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                border: '1px solid var(--cares-line-card)',
                width: '240px',
                zIndex: 50
              }}>
                <div style={{
                  padding: '16px',
                  borderBottom: '1px solid var(--cares-line-card)'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--cares-text-1)',
                    marginBottom: '4px'
                  }}>
                    {user.name}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--cares-text-label)'
                  }}>
                    {user.email}
                  </div>
                </div>
                <div style={{ padding: '8px' }}>
                  <button
                    onClick={() => {
                      setActiveTab('profile');
                      setShowProfileMenu(false);
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      border: 'none',
                      background: 'transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: 'var(--cares-text-3)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <User style={{ width: '16px', height: '16px' }} />
                    My Profile
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('settings');
                      setShowProfileMenu(false);
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      border: 'none',
                      background: 'transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: 'var(--cares-text-3)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <SettingsIcon style={{ width: '16px', height: '16px' }} />
                    Settings
                  </button>
                </div>
                <div style={{
                  padding: '8px',
                  borderTop: '1px solid var(--cares-line-card)'
                }}>
                  <button
                    onClick={logout}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      border: 'none',
                      background: 'transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: 'var(--cares-orange)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut style={{ width: '16px', height: '16px' }} />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab, collapsed, toggleSidebar, userCount = 0, roleCount = 0, personaCount = 0, questionCount = 0 }) => {
  const [expandedSections, setExpandedSections] = React.useState({});
  const isAdminView = activeTab.startsWith("admin");

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const isCurrentlyExpanded = prev[id];
      // If clicking on an already expanded section, just close it
      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [id]: false
        };
      }
      // Otherwise, close all sections and open only this one (accordion behavior)
      return {
        [id]: true
      };
    });
  };

// Normal view menu items
const normalMenuItems = [
  { id: "home", label: "Home", icon: Home },
  { id: "demand-edit", label: "Tasks", icon: ClipboardList },
  {
    id: "ideas",
    label: "Ideas Management",
    icon: Lightbulb,
    children: [
      {
        id: "dashboard",
        label: "Ideas Dashboard",
        icon: BarChart3,
      },
      {
        id: "autoscribe",
        label: "Idea Submission",
        icon: FileText,
      },
      {
        id: "ideas",
        label: "Ideas Management",
        icon: Lightbulb,
      },
      { id: "review", label: "Review Process", icon: ClipboardList },
      {
        id: "guidelines",
        label: "Guidelines & Rules Engine",
        icon: MessageCircle,
      },
      { id: "sentinel", label: "Idea Sentinel", icon: Eye },
      { id: "backlogcurator", label: "Backlog Curator", icon: Archive },
    ],
  },
  {
    id: "demand",
    label: "Demand Management",
    icon: Filter,
    children: [
      {
        id: "demand-dashboard",
        label: "Demand Dashboard",
        icon: BarChart3,
      },
      {
        id: "approved-ideas",
        label: "Approved Ideas",
        icon: CheckCircle,
      },
      {
        id: "triage",
        label: "Triage",
        icon: ShieldAlert,
      },
      {
        id: "discovery",
        label: "Discovery",
        icon: Binoculars,
      },
      {
        id: "aoa",
        label: "Analysis of Alternatives",
        icon: GitCompareArrows,
      },
      {
        id: "poc",
        label: "Enhanced Discovery (POC)",
        icon: ClipboardCheck,
      },
      {
        id: "requirements",
        label: "AI Requirements",
        icon: BookOpenCheck,
      },
      {
        id: "security",
        label: "Security Pre-Screen",
        icon: ShieldCheck,
      },
      {
        id: "tab",
        label: "TAB Decision",
        icon: Gavel,
      },
      {
        id: "execution",
        label: "Execution Handoff",
        icon: Rocket,
      },
      {
        id: "artifacts",
        label: "Stage Artifacts",
        icon: FileText,
      },
      {
        id: "servicenow-tickets",
        label: "ServiceNow Tickets",
        icon: Ticket,
      },
    ],
  },
  {
    id: "bpm-optimization",
    label: "BPM Optimization",
    icon: Workflow,
    children: [
      { id: "bpm", label: "Process Analysis", icon: Activity },
      { id: "bpm-import", label: "Import Process", icon: Upload },
    ],
  },

  {
    id: "reports",
    label: "Reports & Analytics",
    icon: BarChart3,
  },
  {
    id: "investment",
    label: "IT Investment & Project Initiation",
    icon: DollarSign,
  },
  {
    id: "technology",
    label: "Technical Reference Model",
    icon: Cpu,
    children: [
      {
        id: "trm-overview",
        label: "TRM Overview",
        icon: Home,
      },
      {
        id: "trm-analytics",
        label: "Analytics Dashboard",
        icon: PieChart,
      },
      {
        id: "trm-products",
        label: "Product Portfolio",
        icon: Package,
      },
      {
        id: "trm-licenses",
        label: "Software Licenses",
        icon: FileText,
      },
      {
        id: "trm-financial",
        label: "Financial Tracking",
        icon: DollarSign,
      },
      {
        id: "trm-consolidation",
        label: "Consolidation Analysis",
        icon: TrendingUp,
      },
      {
        id: "trm-lifecycle",
        label: "Lifecycle Management",
        icon: RefreshCw,
      },
      {
        id: "trm-taxonomy",
        label: "Taxonomy Browser",
        icon: FolderTree,
      },
      {
        id: "trm-tra",
        label: "CMS TRA Conformance",
        icon: Layers,
      },
      {
        id: "trm-sources",
        label: "Data Sources",
        icon: Database,
      },
      {
        id: "trm-megabyte",
        label: "MEGABYTE & FITARA",
        icon: Scale,
      },
      {
        id: "chatbot",
        label: "Tool Analysis Assistant",
        icon: Bot,
      },
      {
        id: "trm-portal",
        label: "TRM Portal (Legacy)",
        icon: LinkIcon,
      },
      {
        id: "trm-visualization",
        label: "Data Visualization (Legacy)",
        icon: PieChart,
      },
    ],
  },
  {
    id: "cyber",
    label: "Cyber",
    icon: ShieldAlert,
    children: [
      { id: "cyber", label: "Posture & Analyzer", icon: ShieldAlert },
      { id: "cyber-riskmap", label: "Risk Map", icon: Crosshair },
      { id: "cyber-vulnerabilities", label: "COTS Vulnerabilities", icon: Bug },
      { id: "cyber-lifecycle", label: "Out of Support", icon: CalendarClock },
      { id: "cyber-opensource", label: "Open Source Risk", icon: Package },
      { id: "cyber-supply", label: "Supply Chain", icon: Building2 },
      { id: "cyber-sbom", label: "SBOM", icon: FileCode2 },
      { id: "cyber-authorisation", label: "ATO & POA&M", icon: ShieldCheck },
      { id: "cyber-ai", label: "AI Systems", icon: Brain },
      { id: "cyber-trends", label: "Trends", icon: Activity },
    ],
  },
  { id: "chargeback", label: "Chargeback", icon: Landmark },
  {
    id: "budget",
    label: "BudgetAligner",
    icon: Target,
  },
  {
    id: "priority",
    label: "Priority Engine",
    icon: TrendingUp,
  },
];

  // Admin view menu items
  const adminMenuItems = [
    {
      id: "admin",
      label: "Admin",
      icon: Shield,
    },

    {
      id: "admin-workflows",
      label: "Workflows",
      icon: Workflow,
    },
    {
      id: "admin-users",
      label: "User Management",
      icon: Users,
      count: userCount,
    },
    {
      id: "admin-roles",
      label: "Role Management",
      icon: Key,
      count: roleCount,
    },
    {
      id: "admin-persona-management",
      label: "Persona Management",
      icon: Users,
      count: personaCount,
    },
    {
      id: "admin-questionnaire",
      label: "Questionnaire Management",
      icon: ClipboardList,
      count: questionCount,
    },
    {
      id: "admin-guidelines",
      label: "Guidelines & Rules Engine",
      icon: BookOpen,
    },
    {
      id: "admin-mfa",
      label: "MFA Authentication",
      icon: Shield,
    },
    {
      id: "admin-security",
      label: "Security",
      icon: Shield,
      children: [
        {
          id: "admin-signin-logs",
          label: "Sign-in Logs",
          icon: ClipboardList,
        },
        {
          id: "admin-audit-logs",
          label: "Audit Logs",
          icon: ClipboardList,
        },
        {
          id: "admin-data-lineage",
          label: "Data Lineage",
          icon: GitBranch,
        },
      ],
    },
    {
      id: "admin-system-health",
      label: "System Health",
      icon: HeartPulse,
    },
    {
      id: "admin-system-info",
      label: "System Information",
      icon: Info,
    },
  ];

  const renderMenuItems = (items) => {
    return items.map((item) => {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedSections[item.id];
      // Check if parent is active OR if any child is active
      const isActive = activeTab === item.id || (hasChildren && item.children.some(child => child.id === activeTab));

      return (
        <div key={item.id} style={{ marginBottom: '4px' }}>
          <button
            onClick={() => {
              if (hasChildren) {
                if (collapsed) {
                  // If sidebar is collapsed, expand it first
                  toggleSidebar();
                  // Then expand the section
                  setTimeout(() => {
                    toggleSection(item.id);
                  }, 100);
                } else {
                  // If sidebar is already expanded, just toggle the section
                  toggleSection(item.id);
                }
              } else {
                setActiveTab(item.id);
              }
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: collapsed ? '10px' : '10px 12px',
              borderRadius: '8px',
              border: 'none',
              background: isActive ? 'var(--cares-blue)' : 'transparent',
              color: 'var(--cares-text-max)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '14px',
              fontWeight: isActive ? '600' : '400'
            }}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.background = 'transparent';
            }}
            title={collapsed ? item.label : ''}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? '0' : '12px',
              flex: 1,
              justifyContent: collapsed ? 'center' : 'flex-start'
            }}>
              <item.icon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              {!collapsed && (
                <span style={{ flex: 1, textAlign: 'left' }}>
                  {typeof item.label === 'string' ? item.label : item.label}
                  {item.count > 0 && (
                    <span style={{
                      marginLeft: '6px',
                      fontSize: '12px',
                      opacity: 0.8,
                      fontWeight: '400'
                    }}>
                      ({item.count})
                    </span>
                  )}
                </span>
              )}
              {!collapsed && item.badge && (
                <span style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  background: 'rgba(251,191,36,0.2)',
                  color: 'var(--cares-amber)',
                  fontWeight: '600'
                }}>
                  {item.badge}
                </span>
              )}
            </div>
            {!collapsed && hasChildren && (
              <ChevronDown
                style={{
                  width: '16px',
                  height: '16px',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              />
            )}
          </button>

          {/* Render children */}
          {!collapsed && hasChildren && isExpanded && (
            <div style={{
              marginTop: '4px',
              marginLeft: '12px',
              paddingLeft: '12px',
              borderLeft: '2px solid rgba(255,255,255,0.1)'
            }}>
              {item.children.map((child) => {
                const isChildActive = activeTab === child.id;
                return (
                  <button
                    key={child.id}
                    onClick={() => setActiveTab(child.id)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      background: isChildActive ? 'var(--cares-blue)' : 'transparent',
                      color: 'var(--cares-text-max)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '13px',
                      marginBottom: '2px',
                      fontWeight: isChildActive ? '600' : '400'
                    }}
                    onMouseEnter={(e) => {
                      if (!isChildActive) e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isChildActive) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {child.icon && <child.icon style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
                    <span style={{ flex: 1, textAlign: 'left' }}>{child.label}</span>
                    {child.badge && (
                      <span style={{
                        fontSize: '9px',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        background: 'rgba(251,191,36,0.2)',
                        color: 'var(--cares-amber)',
                        fontWeight: '600'
                      }}>
                        {child.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div style={{
      width: collapsed ? '70px' : '280px',
      minHeight: '100vh',
      background: 'linear-gradient(180deg, var(--cares-surface-board) 0%, var(--cares-surface-page) 100%)',
      borderRight: '1px solid var(--cares-line)',
      transition: 'width 0.3s ease',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 30,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      overflowX: 'hidden'
    }}>
      {/* Collapse Toggle Button */}
      <div style={{
        padding: collapsed ? '20px 10px' : '20px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <button
          onClick={toggleSidebar}
          style={{
            width: collapsed ? '40px' : '100%',
            padding: '8px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.1)',
            color: 'var(--cares-text-max)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            fontSize: '13px',
            fontWeight: '500'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
          }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight style={{ width: '16px', height: '16px' }} />
          ) : (
            <>
              <ChevronLeft style={{ width: '16px', height: '16px' }} />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>

      {/* Back button (only shown in admin view) */}
      {isAdminView && !collapsed && (
        <div style={{
          padding: '16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <button
            onClick={() => setActiveTab("dashboard")}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(255,255,255,0.1)',
              color: 'var(--cares-text-max)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '14px',
              fontWeight: '600'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <ArrowLeft style={{ width: '20px', height: '20px' }} />
            <span>Back to Main Menu</span>
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav style={{
        padding: collapsed ? '16px 8px' : '16px',
        flex: 1,
        overflowY: 'auto'
      }}>
        {isAdminView
          ? renderMenuItems(adminMenuItems)
          : renderMenuItems(normalMenuItems)}
      </nav>

      {/* Admin toggle button (only shown in normal view) */}
      {!isAdminView && !collapsed && (
        <div style={{
          padding: '16px',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <button
            onClick={() => setActiveTab("admin")}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(59,130,246,0.1)',
              color: 'var(--cares-blue)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '14px',
              fontWeight: '600'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59,130,246,0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(59,130,246,0.1)'}
          >
            <Shield style={{ width: '20px', height: '20px' }} />
            <span>Admin Panel</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
        status
      )}`}
    >
      {status}
    </span>
  );
};

// Approved Ideas View Component (for Demand Management)
const ApprovedIdeasView = ({ sidebarCollapsed, toggleSidebar, setActiveTab, handlePromoteToDemand }) => {
  const [approvedIdeas, setApprovedIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchApprovedIdeas = async () => {
      try {
        setLoading(true);

        // Fetch all ideas
        const ideasResponse = await fetch('/api/ideas/get_ideas?limit=1000&offset=0');
        const ideasResult = await ideasResponse.json();

        // Fetch all demands to check which ideas are already promoted
        const demandsResponse = await fetch('/api/demands/list');
        const demandsResult = await demandsResponse.json();

        console.log('📊 Demands fetched:', demandsResult);

        // Get set of idea IDs that have been promoted
        const promotedIdeaIds = new Set(
          (demandsResult.demands || []).map(d => d.idea_id).filter(Boolean)
        );

        console.log('🚫 Promoted idea IDs:', Array.from(promotedIdeaIds));

        // Filter for ideas approved by both Supervisor and ITPM (case-insensitive)
        // AND not yet promoted to demand
        const approved = (ideasResult.ideas || []).filter(idea =>
          idea.Status?.toLowerCase() === 'approved' &&
          idea['ITPM Review Status']?.toLowerCase() === 'approved' &&
          !promotedIdeaIds.has(idea.Id)
        );

        console.log('✅ Approved ideas (not promoted):', approved.length);

        setApprovedIdeas(approved);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching approved ideas:', error);
        setLoading(false);
      }
    };

    fetchApprovedIdeas();
  }, []);

  const filteredIdeas = approvedIdeas.filter(idea =>
    idea['Title & Sponsor']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    idea.Id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    idea['Submitted By']?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
      <Header currentTab="approved-ideas" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />

      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Breadcrumb and Header */}
        <DemandPageHeader
          title="Approved Ideas Ready for Promotion"
          description="These ideas have been approved by both Supervisor and ITPM and are ready to be promoted to Demand Management"
          breadcrumbPath="Approved Ideas"
        />

        {/* Stats Card */}
        <div style={{
          background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{approvedIdeas.length}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Approved Ideas</div>
            </div>
            <CheckCircle style={{ width: '48px', height: '48px', opacity: 0.8 }} />
          </div>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ position: 'relative' }}>
            <Search style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px',
              height: '20px',
              color: 'var(--cares-text-note)'
            }} />
            <input
              type="text"
              placeholder="Search by title, ID, or submitter..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 44px',
                border: '1px solid var(--cares-line-card)',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : filteredIdeas.length === 0 ? (
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            border: '1px solid var(--cares-line-card)'
          }}>
            <CheckCircle style={{ width: '48px', height: '48px', color: 'var(--cares-green)', margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
              No Approved Ideas Found
            </h3>
            <p style={{ color: 'var(--cares-text-label)', fontSize: '14px' }}>
              {searchTerm ? 'Try adjusting your search criteria' : 'All approved ideas have been promoted to demands'}
            </p>
          </div>
        ) : (
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid var(--cares-line-card)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'var(--cares-surface-board)', borderBottom: '1px solid var(--cares-line-card)' }}>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>ID</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Title & Sponsor</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Submitted By</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIdeas.map((idea, index) => (
                  <tr key={idea.Id} style={{
                    borderBottom: index < filteredIdeas.length - 1 ? '1px solid var(--cares-line-card)' : 'none',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                  >
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-2)', fontWeight: '500' }}>{idea.Id}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-2)', maxWidth: '400px' }}>
                      <div style={{ fontWeight: '500', marginBottom: '4px' }}>{idea['Title & Sponsor']?.split('\n')[0]?.replace('Title: ', '')}</div>
                      <div style={{ fontSize: '12px', color: 'var(--cares-text-label)' }}>{idea.Purpose?.substring(0, 100)}...</div>
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-2)' }}>{idea['Submitted By']}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-label)' }}>{idea.Date}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <button
                        onClick={() => handlePromoteToDemand(idea)}
                        style={{
                          background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                          color: 'var(--cares-text-max)',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'transform 0.15s, box-shadow 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <Rocket style={{ width: '16px', height: '16px' }} />
                        Promote to Demand
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Ideas Management Component
const IdeaManagement = ({ setActiveTab, setSelectedIdeaId, sidebarCollapsed, toggleSidebar, handlePromoteToDemand }) => {
  const [ideaData, setIdeaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIdeaId] = useState(null);
  const [totalIdeas, setTotalIdeas] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingIdea, setDeletingIdea] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // New: status filter

  // The list rendered idea.Status straight from the database, so a status of
  // 'under_review' displayed as "Under_review". capitalize() fixes the first
  // letter and leaves the underscore, which is how it reached production.
  const STATUS_META = {
    approved:     { label: 'Approved',     bg: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))', fg: 'var(--cares-green)' },
    submitted:    { label: 'Submitted',    bg: 'color-mix(in srgb, var(--cares-purple) 22%, var(--cares-surface-card))', fg: 'var(--cares-purple)' },
    under_review: { label: 'Under Review', bg: 'color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card))', fg: 'var(--cares-blue)' },
    rejected:     { label: 'Rejected',     bg: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))', fg: 'var(--cares-orange)' },
    on_hold:      { label: 'On Hold',      bg: 'color-mix(in srgb, var(--cares-purple) 22%, var(--cares-surface-card))', fg: 'var(--cares-purple)' },
  };
  const statusMeta = (v) =>
    STATUS_META[(v || 'submitted').toLowerCase()] ||
    { label: String(v || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      bg: 'var(--cares-surface-board)', fg: 'var(--cares-text-3)' };
  const [statusCounts, setStatusCounts] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'Date', direction: 'desc' }); // New: status counts

  useEffect(() => {
    const fetchIdeaData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use new PostgreSQL API
        const response = await fetch(`/api/ideas/get_ideas?limit=1000&offset=0`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setIdeaData(result.ideas || []);
        setTotalIdeas(result.total || 0);

        // Calculate status counts
        const counts = {};
        (result.ideas || []).forEach(idea => {
          const status = idea.Status || 'submitted';
          counts[status] = (counts[status] || 0) + 1;
        });
        setStatusCounts(counts);
      } catch (error) {
        console.error("Error fetching idea data:", error);
        setError(
          "Failed to connect to the database. Please check your connection and try again."
        );
        setIdeaData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchIdeaData();
  }, []);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredData = ideaData.filter((idea) => {
    // Apply status filter
    const statusMatch = statusFilter === 'all' || idea.Status === statusFilter;

    // Apply search filter
    const searchMatch =
      idea["Title & Sponsor"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea["Submitted By"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea["Status"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea["Purpose"]?.toLowerCase().includes(searchTerm.toLowerCase());

    return statusMatch && searchMatch;
  });

  // Sort the filtered data
  const sortedData = [...filteredData].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    // Handle date sorting
    if (sortConfig.key === 'Date') {
      const dateA = new Date(aValue);
      const dateB = new Date(bValue);
      return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }

    // Handle string sorting
    const comparison = String(aValue).localeCompare(String(bValue));
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const handleIdClick = (id) => {
    setSelectedIdeaId(id);
    setActiveTab("sentinel");
  };

  const handleCreateNewIdea = () => {
    // Route to autoscribe for new idea creation
    setActiveTab("autoscribe");
  };

  const handleEdit = async (idea) => {
    // Fetch full idea details from database
    try {
      const response = await fetch(`/api/sentinal/get_idea?Id=${idea.Id}`);
      if (response.ok) {
        const data = await response.json();
        setEditingIdea(data[0]);
        setShowEditModal(true);
      } else {
        console.error('Failed to load idea details');
      }
    } catch (error) {
      console.error('Error loading idea:', error);
    }
  };

  const handleSaveEdit = async (updatedIdea) => {
    try {
      const response = await fetch(`/api/ideas/update_idea/${updatedIdea.Id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedIdea),
      });

      if (response.ok) {
        // Refresh the data
        const dataResponse = await fetch(`/api/ideas/get_ideas?limit=1000&offset=0`);
        const result = await dataResponse.json();
        setIdeaData(result.ideas || []);
        setShowEditModal(false);
        setEditingIdea(null);
        // Success - modal will close, no alert needed
      } else {
        console.error('Failed to update idea');
      }
    } catch (error) {
      console.error('Error updating idea:', error);
    }
  };

  const handleDelete = (idea) => {
    setDeletingIdea(idea);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingIdea) return;

    try {
      const response = await fetch(`/api/ideas/delete_idea/${deletingIdea.Id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Refresh the data
        const dataResponse = await fetch(`/api/ideas/get_ideas?limit=1000&offset=0`);
        const result = await dataResponse.json();
        setIdeaData(result.ideas || []);
        setShowDeleteModal(false);
        setDeletingIdea(null);
        // Success - modal will close, no alert needed
      } else {
        console.error('Failed to delete idea');
        // Keep modal open on error
      }
    } catch (error) {
      console.error('Error deleting idea:', error);
      // Keep modal open on error
    }
  };

  const handleRetry = () => {
    const fetchIdeaData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use new PostgreSQL API
        const response = await fetch(`/api/ideas/get_ideas?limit=1000&offset=0`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setIdeaData(result.ideas || []);
        setTotalIdeas(result.total || 0);
      } catch (error) {
        console.error("Error fetching idea data:", error);
        setError(
          "Failed to connect to the database. Please check your connection and try again."
        );
        setIdeaData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchIdeaData();
  };

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="flex items-center justify-center h-96">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Professional Header with Breadcrumbs */}
      <IdeasPageHeader
          title={`Ideas Management ${totalIdeas > 0 ? `(${totalIdeas})` : ''}`}
          description={`Manage and track all submitted ideas • Showing ${startIndex + 1}-${Math.min(endIndex, filteredData.length)} of ${filteredData.length} ${searchTerm ? `(filtered from ${totalIdeas} total)` : ''}`}
          breadcrumbPath="Ideas Management"
        >
          {/* Action buttons in header */}
          {!error && (
            <div className="relative w-80">
              <input
                type="text"
                placeholder="Search ideas by title, submitter, status or purpose..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-300 bg-gray-50 focus:bg-white"
              />
            </div>
          )}
          <button
            onClick={handleCreateNewIdea}
            className="create-idea-btn flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-black text-base font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-3 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Create New Idea
          </button>
        </IdeasPageHeader>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 backdrop-blur-sm bg-opacity-95">

        {error ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <svg
                className="w-10 h-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              No Data Available
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
              {error}
            </p>
            <button
              onClick={handleRetry}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-base font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Retry Connection
            </button>
          </div>
        ) : (
          <>
            {/* Top Pagination Controls */}
            <div className="mb-6 flex items-center justify-between px-6 py-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 shadow-md">
              <div className="flex items-center gap-4">
                <div className="text-sm font-medium text-gray-800">
                  Showing <span className="font-bold text-blue-600">{startIndex + 1}</span> to{' '}
                  <span className="font-bold text-blue-600">{Math.min(endIndex, filteredData.length)}</span> of{' '}
                  <span className="font-bold text-blue-600">{filteredData.length}</span> ideas
                  {searchTerm && (
                    <span className="ml-2 text-indigo-600 font-semibold">
                      (filtered from {totalIdeas} total)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Sort by:</label>
                    <select
                      value={`${sortConfig.key}-${sortConfig.direction}`}
                      onChange={(e) => {
                        const [key, direction] = e.target.value.split('-');
                        setSortConfig({ key, direction });
                      }}
                      className="border-2 border-blue-300 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                    >
                      <option value="Date-desc">Newest First</option>
                      <option value="Date-asc">Oldest First</option>
                      <option value="Id-asc">ID (A-Z)</option>
                      <option value="Id-desc">ID (Z-A)</option>
                      <option value="Title & Sponsor-asc">Title (A-Z)</option>
                      <option value="Title & Sponsor-desc">Title (Z-A)</option>
                      <option value="Submitted By-asc">Submitter (A-Z)</option>
                      <option value="Submitted By-desc">Submitter (Z-A)</option>
                      <option value="Status-asc">Status (A-Z)</option>
                      <option value="Status-desc">Status (Z-A)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label className="text-sm font-medium text-gray-700 mr-1">Status:</label>
                    {[['all', 'All'], ['submitted', 'Submitted'], ['under_review', 'Under Review'],
                      ['approved', 'Approved'], ['rejected', 'Rejected']].map(([v, lbl]) => {
                      const on = statusFilter === v;
                      const n = v === 'all' ? ideaData.length
                              : ideaData.filter((i) => i.Status === v).length;
                      return (
                        <button key={v}
                          onClick={() => { setStatusFilter(v); setCurrentPage(1); }}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                            on ? 'bg-blue-600 text-white border-blue-600'
                               : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                          {lbl} <span className={on ? 'opacity-80' : 'text-gray-400'}>{n}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Per page:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border-2 border-blue-300 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg border-2 border-blue-300 text-sm font-bold text-gray-700 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                  title="First Page"
                >
                  ««
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg border-2 border-blue-300 text-sm font-bold text-gray-700 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                  title="Previous Page"
                >
                  ‹
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm hover:shadow-md ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white border-2 border-blue-600 shadow-lg scale-110'
                            : 'bg-white text-gray-700 border-2 border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg border-2 border-blue-300 text-sm font-bold text-gray-700 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                  title="Next Page"
                >
                  ›
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg border-2 border-blue-300 text-sm font-bold text-gray-700 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                  title="Last Page"
                >
                  »»
                </button>

                <span className="ml-3 text-sm font-bold text-gray-700 bg-white px-4 py-2 rounded-lg border-2 border-blue-300 shadow-sm">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
            </div>

            {/* Compact rows. The previous cards were ~350px tall, so three of
                thirty-two ideas fitted on screen and the rest were a scroll.
                Everything below is one row: identity and status, the metadata
                that varies, purpose, and the actions. */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
              {paginatedData.map((idea, index) => {
                const meta = statusMeta(idea.Status);
                const sup = idea["Review Status"];
                const itpm = idea["ITPM Review Status"];
                const promotable = idea.Status?.toLowerCase() === 'approved'
                  && itpm?.toLowerCase() === 'approved';
                return (
                  <div key={idea.Id || index}
                       className="group px-5 py-3.5 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <button onClick={() => handleIdClick(idea.Id)}
                                  className="font-mono text-xs font-semibold text-blue-600 hover:underline">
                            {idea.Id}
                          </button>
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold"
                                style={{ backgroundColor: meta.bg, color: meta.fg }}>
                            {meta.label}
                          </span>
                          <button onClick={() => handleIdClick(idea.Id)}
                                  className="text-sm font-semibold text-gray-900 hover:text-blue-700 text-left truncate">
                            {idea["Title & Sponsor"]}
                          </button>
                        </div>
                        {idea.Purpose && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-1">{idea.Purpose}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400 flex-wrap">
                          <span>{idea.Date}</span>
                          <span>&middot;</span>
                          <span>{idea["Submitted By"] || 'Unattributed'}</span>
                          {/* Review states are only worth the space once they say
                              something; every idea reading "Pending / Pending"
                              is noise occupying half the row. */}
                          {sup && sup.toLowerCase() !== 'pending' && (
                            <><span>&middot;</span><span>Supervisor: {statusMeta(sup).label}</span></>
                          )}
                          {itpm && itpm.toLowerCase() !== 'pending' && (
                            <><span>&middot;</span><span>ITPM: {statusMeta(itpm).label}</span></>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-0.5 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleIdClick(idea.Id)} title="View details"
                                className="text-gray-500 hover:text-blue-700 p-1.5 rounded-md hover:bg-blue-50">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEdit(idea)} title="Edit idea"
                                className="text-gray-500 hover:text-indigo-700 p-1.5 rounded-md hover:bg-indigo-50">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {promotable && (
                          <button onClick={() => handlePromoteToDemand(idea)} title="Promote to demand"
                                  className="text-gray-500 hover:text-green-700 p-1.5 rounded-md hover:bg-green-50">
                            <Rocket className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => handleDelete(idea)} title="Delete idea"
                                className="text-gray-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            <div className="mt-6 flex items-center justify-between px-4 py-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-semibold">{startIndex + 1}</span> to{' '}
                  <span className="font-semibold">{Math.min(endIndex, filteredData.length)}</span> of{' '}
                  <span className="font-semibold">{filteredData.length}</span> ideas
                  {searchTerm && (
                    <span className="ml-2 text-blue-600">
                      (filtered from {totalIdeas} total)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-sm text-green-600 font-semibold flex items-center mr-4">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  PostgreSQL
                </div>

                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-md border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="First Page"
                >
                  ««
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-md border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Previous Page"
                >
                  ‹
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white border border-blue-600'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-md border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Next Page"
                >
                  ›
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-md border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Last Page"
                >
                  »»
                </button>

                <span className="ml-2 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
            </div>

            {filteredData.length === 0 && ideaData.length > 0 && (
              <div className="text-center py-16">
                <Lightbulb className="w-16 h-16 text-amber-400 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  No ideas found
                </h3>
                <p className="text-lg text-gray-600 mb-8">
                  Try adjusting your search criteria
                </p>
                <button
                  onClick={handleCreateNewIdea}
                  className="flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-base font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-3 focus:ring-emerald-500 focus:ring-offset-2 mx-auto transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Create New Idea
                </button>
              </div>
            )}

            {filteredData.length === 0 && ideaData.length === 0 && (
              <div className="text-center py-16">
                <Lightbulb className="w-16 h-16 text-amber-400 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  No ideas available
                </h3>
                <p className="text-lg text-gray-600 mb-8">
                  No ideas have been submitted yet
                </p>
                <button
                  onClick={handleCreateNewIdea}
                  className="flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-base font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-3 focus:ring-emerald-500 focus:ring-offset-2 mx-auto transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Create New Idea
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingIdea && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900">Edit Idea: {editingIdea.Id}</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingIdea(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <form onSubmit={(e) => {
                e.preventDefault();
                handleSaveEdit(editingIdea);
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Title & Sponsor
                    </label>
                    <input
                      type="text"
                      value={editingIdea["Title & Sponsor"] || ''}
                      onChange={(e) => setEditingIdea({...editingIdea, "Title & Sponsor": e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Purpose
                    </label>
                    <textarea
                      value={editingIdea.Purpose || ''}
                      onChange={(e) => setEditingIdea({...editingIdea, Purpose: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Desired Outcome
                    </label>
                    <textarea
                      value={editingIdea["Desired Outcome"] || ''}
                      onChange={(e) => setEditingIdea({...editingIdea, "Desired Outcome": e.target.value})}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Submitted By
                    </label>
                    <input
                      type="text"
                      value={editingIdea["Submitted By"] || ''}
                      onChange={(e) => setEditingIdea({...editingIdea, "Submitted By": e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={editingIdea.Status || 'submitted'}
                      onChange={(e) => setEditingIdea({...editingIdea, Status: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="submitted">Submitted</option>
                      <option value="under_review">Under Review</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="on_hold">On Hold</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Expected Benefits
                    </label>
                    <textarea
                      value={editingIdea["Expected Benefits"] || ''}
                      onChange={(e) => setEditingIdea({...editingIdea, "Expected Benefits": e.target.value})}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingIdea(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingIdea && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Confirm Deletion</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-700 text-lg mb-4">
                  Are you sure you want to delete this idea?
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm font-semibold text-gray-600 mb-1">Idea ID</p>
                  <p className="text-lg font-bold text-gray-900 mb-3">{deletingIdea.Id}</p>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Title</p>
                  <p className="text-gray-900">{deletingIdea["Title & Sponsor"]}</p>
                </div>
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">
                    <strong>Note:</strong> This will mark the idea as deleted in the database. This action can be reversed by updating the status.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingIdea(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Idea
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatsCard = ({
  title,
  value,
  icon: Icon,
  color,
  subtitle,
  trend,
  style,
  onClick,
  clickable = false
}) => {
  return (
    <div
      style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        border: '1px solid var(--cares-line-card)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.2s',
        cursor: clickable ? 'pointer' : 'default'
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 10px 15px rgba(0,0,0,0.15)';
        if (clickable) {
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
        if (clickable) {
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      <div>
        <p style={{
          fontSize: '14px',
          color: 'var(--cares-text-label)',
          fontWeight: '600',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          {title}
        </p>
        <p style={{
          fontSize: '32px',
          fontWeight: 'bold',
          color: 'var(--cares-text-1)',
          marginBottom: '4px'
        }}>
          {value}
        </p>
        {trend && (
          <p style={{
            fontSize: '12px',
            color: 'var(--cares-text-label)',
            marginTop: '4px'
          }}>
            {trend}
          </p>
        )}
        {clickable && (
          <p style={{
            fontSize: '11px',
            color: 'var(--cares-blue)',
            marginTop: '8px',
            fontWeight: '600'
          }}>
            📊 Click for details
          </p>
        )}
      </div>
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '12px',
        background: `${style?.backgroundColor || color}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Icon style={{ width: '28px', height: '28px', color: style?.backgroundColor || color }} />
      </div>
    </div>
  );
};

// Progress Bar Component
const ProgressBar = ({ percentage, color, label, count }) => {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-lg font-medium text-gray-700 truncate">
            {label}
          </span>
          <span className="text-lg text-gray-500 whitespace-nowrap">
            {count} ideas
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: `${Math.min(percentage, 100)}%`,
              backgroundColor: color,
              maxWidth: "100%",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("home");
  // Process preselected in BPM Optimization → Process Analysis (set after an import).
  const [bpmProcessId, setBpmProcessId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dashboardData, setDashboardData] = useState({
  totalIdeas: 0,
  pendingReview: 0,
  underReview: 0,
  approvedIdeas: 0,
  avgReviewTime: 0,
  approvalRate: 0,
  newIdeas: 0,
  ideasByStatus: [],
  recentActivity: []
});
  const [loading, setLoading] = useState(true);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [selectedIdeaId, setSelectedIdeaId] = useState(null);
  const [userCount, setUserCount] = useState(0);
  const [roleCount, setRoleCount] = useState(0);
  const [personaCount, setPersonaCount] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // State for promotion modal
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [promotingIdea, setPromotingIdea] = useState(null);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [promotionSuccess, setPromotionSuccess] = useState(null);

  // Handle promoting an idea to demand management
  const handlePromoteToDemand = async (idea) => {
    // Check if idea is approved (case-insensitive)
    if (idea.Status?.toLowerCase() !== 'approved' || idea['ITPM Review Status']?.toLowerCase() !== 'approved') {
      alert('Only ideas approved by both Supervisor and ITPM can be promoted to Demand Management.');
      return;
    }

    // Show modal instead of confirm dialog
    setPromotingIdea(idea);
    setShowPromotionModal(true);
    setPromotionSuccess(null);
  };

  const confirmPromotion = async () => {
    if (!promotingIdea) return;

    try {
      setPromotionLoading(true);
      const response = await fetch(`/api/demands/promote/${promotingIdea.Id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_owner: promotingIdea['Submitted By'],
          urgency: 'Medium',
          strategic_tags: [],
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setPromotionSuccess(result);
        setPromotionLoading(false);

        // Auto-close after 3 seconds and navigate to demand dashboard
        setTimeout(() => {
          setShowPromotionModal(false);
          setPromotingIdea(null);
          setPromotionSuccess(null);
          setActiveTab('demand-dashboard');
        }, 3000);
      } else {
        const error = await response.json();
        alert(`Failed to promote idea: ${error.detail || 'Unknown error'}`);
        setPromotionLoading(false);
      }
    } catch (error) {
      console.error('Error promoting idea:', error);
      alert('Error promoting idea to demand. Please try again.');
      setPromotionLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
  try {
    setLoading(true);

    // Try new PostgreSQL endpoint first, fallback to old CSV endpoint
    let data;
    try {
      const response = await fetch('/api/ideas/get_dashboard_stats');
      if (response.ok) {
        const result = await response.json();
        data = {
          totalIdeas: result["Total Ideas"] || 0,
          pendingReview: result["Pending Reviews"] || 0,
          underReview: result["Under Review"] || 0,
          approvedIdeas: result["Approved Ideas"] || 0,
          avgReviewTime: result["Average Review Time (days)"] || 0,
          approvalRate: result["Percent of Ideas Approved"] || 0,
          newIdeas: result["Newly Added"] || 0,
          ideasByStatus: result.ideas_by_status || [],
          recentIdeas: result.recent_ideas || [],
          reviewTimeMeasuredFrom: result["Review Time Measured From"] || 0,
          recentActivity: []
        };
      } else {
        // Fallback to old endpoint
        data = await apiService.getDashboardStats();
      }
    } catch (err) {
      console.log("Using fallback dashboard endpoint");
      data = await apiService.getDashboardStats();
    }

    setDashboardData({
      totalIdeas: data.totalIdeas || 0,
      pendingReview: data.pendingReview || 0,
      underReview: data.underReview || 0,
      approvedIdeas: data.approvedIdeas || 0,
      avgReviewTime: data.avgReviewTime || 0,
      approvalRate: data.approvalRate || 0,
      newIdeas: data.newIdeas || 0,
      ideasByStatus: data.ideasByStatus || [],
      recentActivity: data.recentActivity || []
    });
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
    // Optionally set some error state to show to the user
  } finally {
    setLoading(false);
  }
};

    fetchData();
  }, []);

  // Function to fetch detailed review analytics
  const fetchReviewAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const response = await fetch('/api/ideas/get_review_analytics');
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
        setShowAnalyticsModal(true);
      } else {
        console.error('Failed to fetch analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Fetch counts for admin menu items
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // Fetch user count
        const userResponse = await fetch('/api/user_management/user_data');
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUserCount(userData.length);
        }

        // Fetch role count
        const roleResponse = await fetch('/api/role_management/get_role');
        if (roleResponse.ok) {
          const roleData = await roleResponse.json();
          const roles = JSON.parse(roleData.roles);
          setRoleCount(roles.length);
        }

        // Fetch persona count
        const personaResponse = await fetch('/api/persona_management/get_persona');
        if (personaResponse.ok) {
          const personaData = await personaResponse.json();
          const personas = JSON.parse(personaData.persona);
          setPersonaCount(personas.length);
        }

        // Fetch question count
        const questionResponse = await fetch('/api/question/get_questions');
        if (questionResponse.ok) {
          const questionData = await questionResponse.json();
          setQuestionCount(questionData.length);
        }
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };

    fetchCounts();
  }, []);

  // Render different pages based on active tab
  const renderContent = () => {
    if (activeTab === "home") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'linear-gradient(135deg, var(--cares-surface-board) 0%, color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card)) 50%, color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card)) 100%)', minHeight: '100vh' }}>
          <Header currentTab={activeTab} sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <HomePage setActiveTab={setActiveTab} />
        </div>
      );
    }
    if (activeTab === "profile") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
          <Header currentTab={activeTab} sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <Profile />
        </div>
      );
    }
    if (activeTab === "settings") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
          <Header currentTab={activeTab} sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <Settings />
        </div>
      );
    }

    if (activeTab.startsWith("admin-") || activeTab === "admin") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
          <Header currentTab={activeTab} sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <Admin activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
      );
    }
    if (activeTab === "sentinel") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
          <Header currentTab="sentinel" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <IdeaSentinel
            setActiveTab={setActiveTab}
            selectedId={selectedIdeaId}
            setSelectedIdeaId={setSelectedIdeaId}
          />
        </div>
      );
    }

    if (activeTab === "backlogcurator") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
          <Header currentTab="backlogcurator" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <BacklogCurator
            setActiveTab={setActiveTab}
            selectedId={selectedIdeaId}
            setSelectedIdeaId={setSelectedIdeaId}
          />
        </div>
      );
    }

    if (activeTab === "guidelines") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
          <Header currentTab="guidelines" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <Guidelines setActiveTab={setActiveTab} />
        </div>
      );
    }

    if (activeTab === "review") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
          <Header currentTab="review" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <ReviewProcessComponent
            setActiveTab={setActiveTab}
            selectedId={selectedIdeaId}
            setSelectedIdeaId={setSelectedIdeaId}
          />
        </div>
      );
    }

    if (activeTab === "autoscribe") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
          <Header currentTab="autoscribe" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <AutoScribeForm setActiveTab={setActiveTab} />
        </div>
      );
    }

    if (activeTab === "ideas") {
      return (
        <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
          <Header currentTab="ideas" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <IdeaManagement
            setActiveTab={setActiveTab}
            setSelectedIdeaId={setSelectedIdeaId}
            selectedIdeaId={selectedIdeaId}
            sidebarCollapsed={sidebarCollapsed}
            toggleSidebar={toggleSidebar}
            handlePromoteToDemand={handlePromoteToDemand}
          />
        </div>
      );
    }

  if (activeTab === "demand-dashboard") {
    return (
      <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
        <Header currentTab="demand-dashboard" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <DemandDashboard />
      </div>
    );
  }

  if (activeTab === "approved-ideas") {
    return <ApprovedIdeasView
      sidebarCollapsed={sidebarCollapsed}
      toggleSidebar={toggleSidebar}
      setActiveTab={setActiveTab}
      handlePromoteToDemand={handlePromoteToDemand}
    />;
  }

  // Stage screens take no demandId prop: the selection lives in DemandContext
  // (see src/context/DemandContext.jsx). Passing demandId="DEM-001" here is what
  // made every stage silently revert to the same demand no matter what you picked.
  if (activeTab === "triage") {
      return (
        <div className="flex-1 p-6 bg-gray-50">
          <Header currentTab="triage" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <TriagePage />
       </div>
      );
    }
  if (activeTab === "demand-edit") {
      return (
        <div className="flex-1 p-6 bg-gray-50">
          <Header currentTab="demand-edit" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <TasksPage />
       </div>
      );
    }
  if (activeTab === "discovery") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="discovery" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
      <DiscoveryPage />
      </div>
    );
  }
  if (activeTab === "aoa") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="aoa" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <AoAPage onAdvance={() => setActiveTab("poc")} />
      </div>
    );
  }
   if (activeTab === "poc") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="poc" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
      <POCPage />
      </div>
    );
  }
  if (activeTab === "requirements") {
      return (
        <div className="flex-1 p-6 bg-gray-50">
          <Header currentTab="requirements" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <RequirementsPage />
        </div>
      );
    }
  if (activeTab === "tab") {
      return (
        <div className="flex-1 p-6 bg-gray-50">
          <Header currentTab="tab" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
          <TABPage />
        </div>
    );
  }
  if (activeTab === "security") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="security" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <SecurityPage />
      </div>
    );
  }
  // The three portfolio screens share one component; the nav id selects the tab.
  if (activeTab === "priority" || activeTab === "budget" || activeTab === "investment") {
    const tabFor = { priority: "priority", budget: "budget", investment: "investments" };
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab={activeTab} sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <PortfolioPage initialTab={tabFor[activeTab]} />
      </div>
    );
  }
  if (activeTab === "bpm") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="bpm" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <BpmImprovementPage processId={bpmProcessId} />
      </div>
    );
  }
  if (activeTab === "bpm-import") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="bpm-import" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <BpmImportPage onAnalyze={(id) => { setBpmProcessId(id); setActiveTab("bpm"); }} />
      </div>
    );
  }
  if (activeTab === "chargeback") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="chargeback" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <ChargebackPage />
      </div>
    );
  }
  if (activeTab === "cyber" || activeTab.startsWith("cyber-")) {
    const cyberTab = {
      "cyber": "posture",
      "cyber-riskmap": "riskmap",
      "cyber-vulnerabilities": "vulnerabilities",
      "cyber-lifecycle": "lifecycle",
      "cyber-opensource": "opensource",
      "cyber-supply": "supply",
      "cyber-sbom": "sbom",
      "cyber-authorisation": "authorisation",
      "cyber-ai": "ai",
      "cyber-trends": "trends",
    }[activeTab] || "posture";
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab={activeTab} sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <CyberPage initialTab={cyberTab} />
      </div>
    );
  }
  if (activeTab === "artifacts") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="artifacts" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <DemandArtifactsPage />
      </div>
    );
  }
  if (activeTab === "servicenow-tickets") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="servicenow-tickets" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <ServiceNowTicketsPage />
      </div>
    );
  }
  if (activeTab === "execution") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="execution" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <ExecutionPage />
      </div>
    );

  }
  // TRM Portal (formerly technology)
  if (activeTab === "trm-portal") {
    return (
      <div className="flex-1 bg-gray-50">
        <Technology />
      </div>
    );
  }

  // Tool Analysis Assistant (chatbot)
  if (activeTab === "chatbot") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="chatbot" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <ChatBotPage />
      </div>
    );
  }

  // TRM Data Visualization
  if (activeTab === "trm-visualization") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-visualization" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <DataVisualization />
      </div>
    );
  }

  // TRM v2 - Overview
  if (activeTab === "trm-overview") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-overview" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <TRMOverview />
      </div>
    );
  }

  // TRM v2 - Analytics Dashboard
  if (activeTab === "trm-analytics") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-analytics" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <TRMAnalyticsDashboard />
      </div>
    );
  }

  // TRM v2 - Product Portfolio
  if (activeTab === "trm-products") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-products" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <ProductPortfolio />
      </div>
    );
  }

  // TRM v2 - Software Licenses
  if (activeTab === "trm-licenses") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-licenses" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <SoftwareLicenses />
      </div>
    );
  }

  // TRM v2 - Financial Tracking
  if (activeTab === "trm-financial") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-financial" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <FinancialTracking />
      </div>
    );
  }

  // TRM v2 - Consolidation Analysis
  if (activeTab === "trm-megabyte") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-megabyte" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <MegabytePage />
      </div>
    );
  }

  if (activeTab === "trm-sources") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-sources" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <DataSourcesPage />
      </div>
    );
  }

  if (activeTab === "trm-tra") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-tra" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <TraConformancePage />
      </div>
    );
  }

  if (activeTab === "trm-consolidation") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-consolidation" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <ConsolidationAnalysis />
      </div>
    );
  }

  // TRM v2 - Lifecycle Management
  if (activeTab === "trm-lifecycle") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-lifecycle" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <LifecycleManagement />
      </div>
    );
  }

  // TRM v2 - Taxonomy Browser
  if (activeTab === "trm-taxonomy") {
    return (
      <div className="flex-1 bg-gray-50">
        <Header currentTab="trm-taxonomy" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <TaxonomyBrowser />
      </div>
    );
  }

  if (activeTab === "reports") {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <Header currentTab="reports" sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <ReportsAnalytics />
      </div>
    );
  }



  if (activeTab !== "dashboard") {
    return (
      <div style={{ flex: 1, padding: '24px', background: 'var(--cares-surface-board)' }}>
        <Header currentTab={activeTab} sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} setActiveTab={setActiveTab} />
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Page
            </h2>
            <p className="text-gray-600 mb-6">
              This page will be connected to your FastAPI endpoints for{" "}
              {activeTab} functionality.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Integration Point:</strong> Connect to your FastAPI
                endpoint for {activeTab} data
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="flex-1 p-6">
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        flex: 1,
        padding: '24px',
        background: 'linear-gradient(135deg, var(--cares-surface-board) 0%, color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card)) 50%, color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card)) 100%)',
        minHeight: '100vh'
      }}>
        <Header
          currentTab={activeTab}
          sidebarCollapsed={sidebarCollapsed}
          toggleSidebar={toggleSidebar}
          setActiveTab={setActiveTab}
        />

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '20px',
          marginBottom: '24px',
          marginTop: '32px'
        }}>
  <StatsCard
    title="Total Ideas"
    value={dashboardData.totalIdeas}
    icon={Lightbulb}
    style={{ backgroundColor: 'var(--cares-blue)' }}
    trend={`Active ideas in system`}
  />
  <StatsCard
    title="Pending Review"
    value={dashboardData.pendingReview}
    icon={Clock}
    style={{ backgroundColor: 'var(--cares-amber)' }}
    trend={`Awaiting first review`}
  />
  <StatsCard
    title="Under Review"
    value={dashboardData.underReview || 0}
    icon={Eye}
    style={{ backgroundColor: 'var(--cares-teal)' }}
    trend={`Awaiting supervisor decision`}
  />
  <StatsCard
    title="Approved Ideas"
    value={dashboardData.approvedIdeas}
    icon={CheckCircle}
    style={{ backgroundColor: 'var(--cares-green)' }}
    trend={`${dashboardData.approvalRate || 0}% approval rate`}
  />
  <StatsCard
    title="Median Review Time"
    value={dashboardData.avgReviewTime == null ? "—" : `${dashboardData.avgReviewTime} days`}
    icon={TrendingUp}
    style={{ backgroundColor: 'var(--cares-purple)' }}
    trend={dashboardData.avgReviewTime == null
        ? "Not yet measured — no idea carries both review dates"
        : `Based on ${dashboardData.reviewTimeMeasuredFrom || 0} fully reviewed ideas`}
    onClick={fetchReviewAnalytics}
    clickable={true}
  />
</div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginBottom: '24px'
        }}>
          {/* Ideas by Status Section */}
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            border: '1px solid var(--cares-line-card)'
          }}>
  <div style={{ marginBottom: '24px' }}>
    <h3 style={{
      fontSize: '20px',
      fontWeight: '600',
      color: 'var(--cares-text-1)',
      marginBottom: '4px'
    }}>
      Ideas by Status
    </h3>
    <p style={{
      fontSize: '14px',
      color: 'var(--cares-text-label)'
    }}>
      Current distribution of ideas across review stages
    </p>
  </div>
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    {dashboardData.ideasByStatus.length > 0 ? (
      dashboardData.ideasByStatus.map((item, index) => {
        const percentage = dashboardData.totalIdeas > 0
          ? Math.min(100, (item.count / dashboardData.totalIdeas) * 100)
          : 0;
        return (
          <div key={index} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)' }}>
                {item.status}
              </span>
              <span style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
                {item.count} ideas
              </span>
            </div>
            <div style={{ width: '100%', background: 'var(--cares-line-card)', borderRadius: '9999px', height: '8px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${percentage}%`,
                  maxWidth: '100%',
                  height: '8px',
                  borderRadius: '9999px',
                  backgroundColor: item.color,
                  transition: 'all 0.3s'
                }}
              />
            </div>
          </div>
        );
      })
    ) : (
      <p style={{ color: 'var(--cares-text-label)', fontSize: '14px' }}>No status data available</p>
    )}
  </div>
</div>

          {/* Quick Actions Section */}
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            border: '1px solid var(--cares-line-card)'
          }}>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'var(--cares-text-1)',
                marginBottom: '4px'
              }}>
                Quick Actions
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'var(--cares-text-label)'
              }}>
                Frequently used actions and shortcuts
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => setActiveTab("autoscribe")}
                style={{
                  width: '100%',
                  background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                  border: '2px solid color-mix(in srgb, var(--cares-blue) 30%, var(--cares-surface-card))',
                  color: 'var(--cares-blue)',
                  padding: '16px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '15px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card))';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))';
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--cares-blue)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--cares-text-max)',
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}>
                  +
                </div>
                <span>Submit New Idea</span>
              </button>
              <button
                onClick={() => setActiveTab("demand-dashboard")}
                style={{
                  width: '100%',
                  background: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))',
                  border: '2px solid color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))',
                  color: 'var(--cares-green)',
                  padding: '16px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '15px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))';
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--cares-green)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BarChart3 style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                </div>
                <span>View Demand Dashboard</span>
              </button>
              <button
                onClick={() => setActiveTab("review")}
                style={{
                  width: '100%',
                  background: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))',
                  border: '2px solid color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))',
                  color: 'var(--cares-amber)',
                  padding: '16px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '15px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))';
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--cares-amber)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ClipboardList style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                </div>
                <span>Review Pending Ideas</span>
              </button>
            </div>
          </div>
        </div>

        {/* Recent Ideas — the page previously showed 32 ideas and none of them.
              Counts answer "how many"; this answers "which", which is the
              question someone opening this page actually has. */}
          <div className="lg:w-1/2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4 flex items-baseline justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Recent Ideas</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Latest submissions, newest first
                </p>
              </div>
              <button
                onClick={() => setActiveTab("ideas")}
                className="text-sm font-medium text-indigo-600 hover:underline">
                View all {dashboardData.totalIdeas || 0} &rarr;
              </button>
            </div>
            <div className="space-y-2">
              {(dashboardData.recentIdeas || []).length > 0 ? (
                dashboardData.recentIdeas.map((idea) => (
                  <div key={idea.ideaId}
                       className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                    <span style={{ background: idea.color }}
                          className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {idea.title}
                        </p>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                              style={{ color: idea.color, background: `${idea.color}18` }}>
                          {idea.statusLabel}
                        </span>
                      </div>
                      {idea.purpose && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{idea.purpose}</p>
                      )}
                      <p className="text-[11px] text-gray-400 mt-1 font-mono">
                        {idea.ideaId}
                        {idea.submittedOn ? ` \u00b7 ${idea.submittedOn}` : ""}
                        {idea.submittedBy ? ` \u00b7 ${idea.submittedBy}` : ""}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-sm">No ideas recorded yet.</div>
              )}
            </div>
          </div>

          {/* Review Analytics Modal */}
        {showAnalyticsModal && analyticsData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowAnalyticsModal(false)}
        >
          <div
            style={{
              background: 'var(--cares-surface-card)',
              borderRadius: '16px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--cares-line-card)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
              borderRadius: '16px 16px 0 0'
            }}>
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--cares-text-max)', marginBottom: '4px' }}>
                  📊 Review Time Analytics
                </h2>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>
                  Detailed breakdown of approval timelines
                </p>
              </div>
              <button
                onClick={() => setShowAnalyticsModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--cares-text-max)',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Summary Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px',
                marginBottom: '32px'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
                  borderRadius: '12px',
                  padding: '20px',
                  color: 'var(--cares-text-max)'
                }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '8px', fontWeight: '600' }}>MEDIAN</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{analyticsData.summary.median_days}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px' }}>days</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                  borderRadius: '12px',
                  padding: '20px',
                  color: 'var(--cares-text-max)'
                }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '8px', fontWeight: '600' }}>AVERAGE</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{analyticsData.summary.average_days}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px' }}>days</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                  borderRadius: '12px',
                  padding: '20px',
                  color: 'var(--cares-text-max)'
                }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '8px', fontWeight: '600' }}>FASTEST</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{analyticsData.summary.min_days}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px' }}>days</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, var(--cares-orange) 0%, var(--cares-orange) 100%)',
                  borderRadius: '12px',
                  padding: '20px',
                  color: 'var(--cares-text-max)'
                }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '8px', fontWeight: '600' }}>SLOWEST</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{analyticsData.summary.max_days}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px' }}>days</div>
                </div>
              </div>

              {/* Percentiles */}
              <div style={{
                background: 'var(--cares-surface-board)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cares-text-2)', marginBottom: '16px' }}>
                  📈 Percentiles
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '4px' }}>50th Percentile (Median)</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--cares-text-2)' }}>{analyticsData.summary.median_days} days</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '4px' }}>75th Percentile</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--cares-text-2)' }}>{analyticsData.summary.p75_days} days</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '4px' }}>90th Percentile</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--cares-text-2)' }}>{analyticsData.summary.p90_days} days</div>
                  </div>
                </div>
              </div>

              {/* Ideas Breakdown Table */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cares-text-2)', marginBottom: '16px' }}>
                  📋 Breakdown by Idea ({analyticsData.summary.total_approved} approved ideas)
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--cares-surface-board)', borderBottom: '2px solid var(--cares-line-card)' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Idea ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Title</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Submitted</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Approved</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Review Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.ideas.map((idea, index) => (
                        <tr key={idea.idea_id} style={{ borderBottom: '1px solid var(--cares-line-card)' }}>
                          <td style={{ padding: '12px', fontSize: '13px', color: 'var(--cares-blue)', fontWeight: '500' }}>{idea.idea_id}</td>
                          <td style={{ padding: '12px', fontSize: '13px', color: 'var(--cares-text-2)', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{idea.title}</td>
                          <td style={{ padding: '12px', fontSize: '13px', color: 'var(--cares-text-label)' }}>{idea.submission_date}</td>
                          <td style={{ padding: '12px', fontSize: '13px', color: 'var(--cares-text-label)' }}>{idea.final_approval_date}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            <span style={{
                              background: idea.review_days === 0 ? 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))' : idea.review_days <= 2 ? 'color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card))' : idea.review_days <= 7 ? 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))' : idea.review_days <= 30 ? 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))' : 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
                              color: idea.review_days === 0 ? 'var(--cares-green)' : idea.review_days <= 2 ? 'var(--cares-blue)' : idea.review_days <= 7 ? 'var(--cares-amber)' : idea.review_days <= 30 ? 'var(--cares-orange)' : 'var(--cares-orange)',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              {idea.review_days} {idea.review_days === 1 ? 'day' : 'days'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cares-surface-board)' }}>
      <div style={{ display: 'flex' }}>
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          collapsed={sidebarCollapsed}
          toggleSidebar={toggleSidebar}
          userCount={userCount}
          roleCount={roleCount}
          personaCount={personaCount}
          questionCount={questionCount}
        />
        <div style={{
          flex: 1,
          marginLeft: sidebarCollapsed ? '70px' : '280px',
          transition: 'margin-left 0.3s ease',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {renderContent()}
        </div>
      </div>

      {/* Promotion Modal */}
      {showPromotionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            backgroundColor: 'var(--cares-surface-card)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            {!promotionSuccess ? (
              <>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Rocket size={32} color="var(--cares-text-max)" />
                  </div>
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: 'var(--cares-text-1)',
                    marginBottom: '8px'
                  }}>
                    Promote to Demand Management
                  </h2>
                  <p style={{ color: 'var(--cares-text-label)', fontSize: '14px' }}>
                    This will create a new demand and start the 7-stage screening workflow
                  </p>
                </div>

                {/* Idea Details */}
                <div style={{
                  backgroundColor: 'var(--cares-surface-board)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  border: '1px solid var(--cares-line-card)'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '500' }}>IDEA ID</span>
                    <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '600', marginTop: '4px' }}>
                      {promotingIdea?.Id}
                    </p>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '500' }}>TITLE</span>
                    <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', marginTop: '4px' }}>
                      {promotingIdea?.['Title & Sponsor']}
                    </p>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '500' }}>SUBMITTED BY</span>
                    <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', marginTop: '4px' }}>
                      {promotingIdea?.['Submitted By']}
                    </p>
                  </div>
                </div>

                {/* Next Steps */}
                <div style={{
                  backgroundColor: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '24px',
                  border: '1px solid color-mix(in srgb, var(--cares-blue) 30%, var(--cares-surface-card))'
                }}>
                  <p style={{ fontSize: '13px', color: 'var(--cares-blue)', fontWeight: '500', marginBottom: '8px' }}>
                    📋 What happens next:
                  </p>
                  <ul style={{ fontSize: '13px', color: 'var(--cares-blue)', paddingLeft: '20px', margin: 0 }}>
                    <li>New demand will be created in the system</li>
                    <li>Workflow starts at <strong>Triage</strong> stage</li>
                    <li>You'll be redirected to Demand Dashboard</li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => {
                      setShowPromotionModal(false);
                      setPromotingIdea(null);
                    }}
                    disabled={promotionLoading}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: '1px solid var(--cares-line-strong)',
                      backgroundColor: 'var(--cares-surface-card)',
                      color: 'var(--cares-text-3)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: promotionLoading ? 'not-allowed' : 'pointer',
                      opacity: promotionLoading ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => !promotionLoading && (e.target.style.backgroundColor = 'var(--cares-surface-board)')}
                    onMouseLeave={(e) => !promotionLoading && (e.target.style.backgroundColor = 'var(--cares-surface-card)')}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmPromotion}
                    disabled={promotionLoading}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      background: promotionLoading
                        ? 'var(--cares-surface-raised)'
                        : 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                      color: 'var(--cares-text-max)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: promotionLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {promotionLoading ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid var(--cares-line-card)',
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite'
                        }} />
                        Promoting...
                      </>
                    ) : (
                      <>
                        <Rocket size={16} />
                        Confirm Promotion
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              /* Success State */
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  animation: 'scaleIn 0.5s ease-out'
                }}>
                  <CheckCircle size={48} color="var(--cares-text-max)" />
                </div>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: 'var(--cares-text-1)',
                  marginBottom: '12px'
                }}>
                  Successfully Promoted!
                </h2>
                <p style={{ color: 'var(--cares-text-label)', fontSize: '14px', marginBottom: '20px' }}>
                  Idea has been promoted to Demand Management
                </p>
                <div style={{
                  backgroundColor: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))',
                  marginBottom: '20px'
                }}>
                  <p style={{ fontSize: '13px', color: 'var(--cares-green)', marginBottom: '8px' }}>
                    <strong>Demand ID:</strong> {promotionSuccess.demand_id}
                  </p>
                  <p style={{ fontSize: '13px', color: 'var(--cares-green)' }}>
                    <strong>Next Stage:</strong> {promotionSuccess.next_stage}
                  </p>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--cares-text-note)' }}>
                  Redirecting to Demand Dashboard...
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};
export default Dashboard;


